//  External
import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, forkJoin, Subject, timer, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { catchError, takeUntil } from 'rxjs/operators';

//  Internal
import { ConnectNotificationsService } from '../../common/services/requests/connect-notifications/connect-notifications.service';
import { DevicesService } from '../../common/services/process/devices/devices.service';
import { INotificationsService } from '../../common/services/interfaces/inotifications/inotifications.service';
import { NotificationDrawerStatus, NotificationsService } from '../../common/services/process/notifications/notifications.service';
import { ToastService } from '../../common/services/global/toast/toast.service';
import { UsefulService } from '../../common/services/global/useful/useful.service';
import { MessageService } from '../../common/services/core/message.service';
import { ValuesService } from '../../common/values/values.service';
import { UtilsCommonService } from '../../common/utils/utils-common.service';
import { AdobeDataLayerService } from '../../common/services/core/adobe.datalayer.service';
import { NotificationsTypes, NotificationsSubtypes } from '../../common/services/global/events/events.model';
import { PwaNotificationsService } from '../../common/services/global/pwa-notifications/pwa-notifications.service';
import { NotificationActions } from '../../common/models/Pwa.model';

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss']
})

export class NotificationsComponent implements OnInit, OnDestroy, OnChanges {

    @Input() open = false;
    @Output() openChange: EventEmitter<boolean> = new EventEmitter();
    @Output() notificationDetailsEmiter: EventEmitter<{}> = new EventEmitter();
    @Output() statusDrawer: EventEmitter<string> = new EventEmitter();

    public tsReady = false;
    devices: any;
    exceptions = [];
    notificationDetails: any = {};
    notifs: any;
    languageChanged = false;
    notifsGrouped = [];
    showNotificationDetails = false;
    showNotificationsList = false;
    public readonly notificationTypeIssues = NotificationsTypes.ISSUES;
    public readonly notificationTypeParental = NotificationsTypes.PARENTAL;
    public readonly notificationTypeNcc = NotificationsTypes.NCC;
    public readonly notificationSubtypeNeedMoreTime = NotificationsSubtypes.NEED_MORE_TIME;

    readonly cleanNotifs: Subscription[] = [];
    public destroyed = new Subject<any>();
    readonly onDestroy$: Subject<void> = new Subject<void>();
    private timer$: Subscription;

    private skipShowList = false;

    constructor(
        private readonly connectNotificationsService: ConnectNotificationsService,
        private readonly devicesService: DevicesService,
        private readonly iNotificationsService: INotificationsService,
        private readonly notificationsService: NotificationsService,
        private readonly toastService: ToastService,
        private readonly translateService: TranslateService,
        private readonly messageService: MessageService,
        private readonly utilsCommonService: UtilsCommonService,
        public readonly usefulService: UsefulService,
        private readonly valuesService: ValuesService,
        private readonly router: Router,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        private readonly pwaNotificationsService: PwaNotificationsService
    ) {
        this.messageService.getMessage()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(message => {
            switch (message.text) {
                case this.valuesService.events.dataResolverDone:
                    this.start();
                    break;
                case this.valuesService.events.languageChange:
                    if (this.showNotificationsList || this.showNotificationDetails) {
                        this.languageChanged = true;
                    }
                    break;
                case this.valuesService.events.createPersistentNotification:
                    this.handlePersistentNotificationCreation(message.options);
                    break;
                default:
                    break;
            }
        });
    }

    ngOnInit() {
        this.notificationsService.invokeEventForNotifs
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
            value => {
                if (value === true) {
                    this.start();
                }
            }
        );

        this.notificationsService.getShowNotificationDetailsEvent()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
            notification => {
                this.handleNotificationsDetailsOpening(notification.data);
            }
        );

        this.notificationDetailsEmiter.subscribe(
            () => {
                // ! new event page omniture notifications
                // ! set drawer status to open with details and trigger adobe page view
                this.notificationsService.setDrawerStatus(NotificationDrawerStatus.DETAILS);
                //!
            }
        )
    }

    ngOnChanges(changes: SimpleChanges) {
        // ! new event page omniture notifications
        // ! drawer open/closed -> null/list values
        // adobe page view must be triggered
        if (changes.open && changes.open.currentValue) {
            this.adobeDataLayerService.removeRecommendationCardScenarioEvent();
            this.notificationsService.setDrawerStatus(NotificationDrawerStatus.LIST);
            this.adobeDataLayerService.setPageSection(this.valuesService.omnitureNotificationsSections.notifications, 0, true);
        // when central is firs loaded, ngOnChanges is triggered
        // adobe must not re-init when that happens because it is to be triggered
        // only when open/close notificaitons is performed
        } else if (changes.open.previousValue !== undefined) {
            this.notificationsService.setDrawerStatus(NotificationDrawerStatus.NULL);
            this.adobeDataLayerService.setPageSection(null, 0, true);
        }
        //!

        if (changes.open && !changes.open.firstChange && !this.skipShowList) {
            this.showList(true);
        }

        if (changes.open.currentValue) {
            this.timer$ = timer(0, 1000)
                .subscribe(() => {
                    this.computeTimeAgo();
                });
        } else {
            if (this.timer$ !== undefined) {
                this.timer$.unsubscribe();
            }
        }
    }

    ngOnDestroy(): void {
        if (this.timer$ !== undefined) {
            this.timer$.unsubscribe();
        }
        this.destroyed.next(undefined);
        this.destroyed.complete();
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    /**
     * Method used for handling the createPersistentNotification message sent by the service worker.
     * @param message
     */
    private handlePersistentNotificationCreation(notification) {
        this.iNotificationsService.createNotificationDetails(notification);
        this.pwaNotificationsService.showPersistentNotification(
            notification.user_fields.text,
            {
                timestamp: notification.app_fields.timestamp,
                actions: [{
                    action: NotificationActions.NOTIFICATION_DETAILS,
                    title: 'Show me more'
                }],
                data: notification
            }
        );
    }

    /**
     * Method used for handling the NOTIFICATION_CLICK message sent by the service worker.
     * @param {string} id - notification id
     */
    private handleNotificationsDetailsOpening(notification) {
        this.skipShowList = true;
        this.showNotificationDetails = false;
        this.showNotificationsList = true;
        this.toggleDetails(notification);
        this.openChange.emit(true);
    }

    /**
    * Toggles right sidenav notifications menu
    * @param {none}
    * @returns {nothing}
    */
    closeAppDrawerNotif() {
        this.statusDrawer.emit('close');
    }

    //  marks all notifications as read
    //  verifica functia asta
    markNotificationsAsRead() {
        this.connectNotificationsService.readAllNotifications()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
            next: () => {
                for (const notification of this.notifs.list) {
                    notification.ack = true;
                }
                this.notifs.count = 0;
                this.messageService.sendMessage(this.valuesService.events.notificationsCounterChange, { id: 'val', data: {} });
            },
            error: () => {
                this.toastService.error(this.translateService.instant('activity.error.msg'));
            }
        });
    }

    //  deletes all notifications
    deleteNotifications() {
        this.connectNotificationsService.deleteNotifications()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
            next: () => {
                this.notifs.list = [];
                this.notifs.count = 0;
                this.messageService.sendMessage(this.valuesService.events.notificationsCounterChange, { id: 'val', data: {} });
            },
            error: () => {
                this.toastService.error(this.translateService.instant('activity.error.msg'));
            }
        });
    }

    //  opens drawer with list of notifications
    showList(restart) {

        this.showNotificationDetails = false;
        this.showNotificationsList = true;
        if (restart) {
            this.start();
        }
    }

    moreListItems() {
        this.notificationsService.listNotifications(true)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
            next: () => {
                this.notificationsService.listThreatsInfo()
                .pipe(takeUntil(this.onDestroy$))
                .subscribe({
                    next: () => {
                        this.iNotificationsService.createNotificationsDetails();
                        this.notifs = this.notificationsService.get();
                        this.notifsGrouped = this.groupNotifications(this.notifs.list);
                    },
                    error: () => { }
                });
            },
            error: () => {
                this.toastService.error(this.translateService.instant('activity.error.msg'));
            }
        });
    }

    toggleDetails(notification) {
        if (notification?.app_fields?.notification_params?.notification_type === NotificationsTypes.ISSUES) {
            this.navigateToDeviceDetails(notification);
        } else if ((notification?.app_fields?.notification_params?.notification_type === NotificationsTypes.PARENTAL
                    || notification?.app_fields?.notification_params?.notification_type === NotificationsTypes.NCC)
                    && notification?.app_fields?.notification_params?.notification_subtype === NotificationsSubtypes.NEED_MORE_TIME) {
            this.navigateToNccChildProfile(notification);
        } else {
            this.showNotificationDetails = !this.showNotificationDetails;
            this.showNotificationsList = !this.showNotificationsList;

            this.notificationDetails = notification;
            this.notificationDetailsEmiter.emit(notification);
        }

    }

    navigateToDeviceDetails(notification) {
        const deviceId = notification?.interface?.device_id;
        const idHash = this.utilsCommonService.crc32Convert_old(deviceId);
        this.notificationsService.readNotification(notification).pipe(takeUntil(this.onDestroy$)).subscribe();
        this.closeAppDrawerNotif();
        this.router.navigate([this.valuesService.centralPaths.devices.path, idHash, this.valuesService.centralPaths.devices.details.id]);
    }

    navigateToNccChildProfile(notification) {
        const profileId = notification?.interface?.profile_id;
        const idHash = this.utilsCommonService.crc32Convert_old(profileId);
        this.notificationsService.readNotification(notification).pipe(takeUntil(this.onDestroy$)).subscribe();
        this.closeAppDrawerNotif();
        this.router.navigate([this.valuesService.centralPaths.parentalncc.path, idHash]);
    }

    groupNotifications(ntfs) {
        let final_flags = [];
        let current_day: any = new Date();
        current_day.setHours(23, 59, 59);

        for (const i in ntfs) {
            let timestamp: number = parseInt(((current_day - new Date(ntfs[i].app_fields.timestamp).getTime()) / (60 * 60 * 24 * 1000)).toString(), 10);
            let temp = final_flags.filter(x => x.days_ago === timestamp);
            if (temp.length === 0) {
                final_flags.push({
                    days_ago: timestamp,
                    notifs: []
                });
            }
            final_flags.filter(x => x.days_ago === timestamp)[0].notifs.push(ntfs[i]);
        }

        return final_flags;
    }

    /**
    * Computes timeAgo for each notification
    * @param {none}
    * @returns {nothing}
    */
    private computeTimeAgo(): void {
        for (const group of this.notifsGrouped) {
            for (const notification of group.notifs) {
                notification.app_fields.time_ago = this.usefulService.timeAgo(notification.app_fields.timestamp);
            }
        }
    }

    start() {
        this.notificationsService.listNotifications()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
            next: () => {
                return forkJoin([
                    this.notificationsService.countUnreadNotifications()
                    .pipe(
                        catchError(() => {
                            return of(true);
                        })
                    ),
                    this.notificationsService.listThreatsInfo(),
                    this.devicesService.listExceptions()
                    .pipe(
                        catchError(() => {
                            return of(true);
                        })
                    )
                ])
                .pipe(takeUntil(this.onDestroy$))
                .subscribe({
                    next: () => {
                        this.devices = this.devicesService.getDevicesList().all;
                        this.exceptions = this.devicesService.getExceptionsList();

                        this.iNotificationsService.createNotificationsDetails();
                        this.languageChanged = false;
                        this.notifs = this.notificationsService.get();
                        this.notifsGrouped = this.groupNotifications(this.notifs.list);
                        this.computeTimeAgo();

                        // cand citesti o notificare necitita, si esti in details,
                        // sau cand vine orice alt event si esti in details
                        // ar trebui sa ramai acolo si dupa ce se face triggerStart
                        // ca sa se citeasca notificarea
                        if (!this.showNotificationDetails) {
                            this.showNotificationsList = true;
                        }
                        this.tsReady = true;
                    },
                    error: () => {
                        this.showNotificationsList = true;
                        this.notifs = {};
                        this.notifs.list = [];
                        this.tsReady = true;
                    }
                });
            },
            error: () => {
                this.showNotificationsList = true;
                this.notifs = {};
                this.notifs.list = [];
                this.tsReady = true;
            }
        });
    }

}
